import { useContext, useEffect, useState } from "react";
import { ChapterFunctionary, Member, Person } from "../types/apiTypes";
import { DataContext } from "./AppRouter";
import PersonActions from "./PersonActions";
import PersonBoxes from "./PersonBoxes";
import useFetchCommittee from "./hooks/useFetchCommittee";
import useFetchMember from "./hooks/useFetchMember";
import Loading from "./ui/Loading";

interface Props {
  isCommittee?: boolean;
}

const PersonList = ({ isCommittee = false }: Props) => {
  const { chapter } = useContext(DataContext);
  const fetchedPersons = isCommittee
    ? useFetchCommittee(chapter)
    : useFetchMember(chapter);

  const [inputValue, setInputValue] = useState("");
  const [originalPersons, setOriginalPersons] = useState<Person[]>();
  const [persons, setPersons] = useState<Person[]>();

  useEffect(() => {
    setPersons(undefined);
  }, [chapter]);

  useEffect(() => {
    if (!fetchedPersons) return;

    const typedPersons = getTypedPersons(fetchedPersons);

    setTimeout(() => {
      setOriginalPersons(typedPersons);
      setPersons(typedPersons);
    }, 350);
  }, [fetchedPersons]);

  useEffect(() => {
    setPersons(filterPersonByQuery(originalPersons, inputValue));
  }, [inputValue]);

  return !persons ? (
    <Loading />
  ) : (
    <>
      <PersonActions
        items={persons}
        inputValue={inputValue}
        setInputValue={setInputValue}
        isCommittee={isCommittee}
      />
      {persons.length ? (
        <PersonBoxes items={persons} showImage={false} />
      ) : (
        <div>Keine Ergebnisse</div>
      )}
    </>
  );
};

export default PersonList;

const filterPersonByQuery = (
  personList: Person[] | undefined,
  inputValue: string,
) =>
  personList?.filter(
    (person) =>
      person.name?.toLowerCase().includes(inputValue.toLowerCase()) ||
      person.lastName?.toLowerCase().includes(inputValue.toLowerCase()) ||
      person.companyName?.toLowerCase().includes(inputValue.toLowerCase()),
  );

const isChapterFunctionary = (
  person: ChapterFunctionary | Member,
): person is ChapterFunctionary =>
  (person as ChapterFunctionary).person !== undefined;

const getTypedPersons = (persons: ChapterFunctionary[] | Member[]): Person[] =>
  persons.map((item) => {
    const isFunctionary = isChapterFunctionary(item);

    return {
      name: isFunctionary
        ? item.person.firstName
        : item.legalRepresentative.firstName,
      lastName: isFunctionary
        ? item.person.lastName
        : item.legalRepresentative.lastName,
      email: isFunctionary ? item.person.email : item.email,
      phone: isFunctionary ? undefined : item.phone,
      mobile: isFunctionary ? item.person.mobile : undefined,
      street: isFunctionary ? item.person.street : item.street,
      zip: isFunctionary ? item.person.zip : item.zip,
      city: isFunctionary ? item.person.city : item.city,
      companyName: item.company,
      id: isFunctionary ? item.id : null,
      picture: isFunctionary ? item.person.picture : null,
      roles: isFunctionary ? item.roles : null,
      website: isFunctionary ? undefined : item.website,
    };
  });
